/**
 * safeStorage — defensive wrapper around `window.localStorage`.
 *
 * Why this exists
 *   • In private / incognito modes (especially Safari) localStorage can be a
 *     no-op proxy, or it can THROW on every write (QuotaExceededError).
 *   • SSR / pre-render environments expose no `window` at all.
 *   • Catching the errors per-callsite spreads identical try/catch boilerplate
 *     across every component that ever stores a preference.
 *
 * Security note
 *   • Never store JWTs, refresh tokens, or PII via this helper. Auth tokens
 *     live in HTTP-only cookies set by the backend (see /app/backend/app/
 *     routers/auth.py `set_auth_cookie`). This wrapper is intended only for
 *     UI preferences (language, dismissed banners, the public share-token
 *     used for shareable booking links).
 *
 * API
 *   safeStorage.get(key, fallback?)        — returns fallback on failure / SSR.
 *   safeStorage.set(key, value)            — returns true if persisted, else false.
 *   safeStorage.remove(key)                — returns true if attempted, else false.
 *   safeStorage.setWithExpiry(key, val, ms)— stores {v, exp} JSON envelope.
 *   safeStorage.getWithExpiry(key)         — returns null if missing/expired.
 *
 * All methods are SSR-safe and never throw.
 */

const hasStorage = (() => {
  try {
    if (typeof window === "undefined") return false;
    if (!window.localStorage) return false;
    // Probe — Safari private mode raises here.
    const probeKey = "__trel_storage_probe__";
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
})();

const safeStorage = {
  get(key, fallback = null) {
    if (!hasStorage) return fallback;
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? fallback : raw;
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    if (!hasStorage) return false;
    try {
      window.localStorage.setItem(key, String(value));
      return true;
    } catch {
      return false;
    }
  },
  remove(key) {
    if (!hasStorage) return false;
    try {
      window.localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
  /**
   * Store a value with a TTL (in milliseconds). On read, expired entries
   * are auto-evicted so the key disappears from storage as well as the
   * read returning null. Useful for one-time tokens (share links, dismissed
   * banner timers, etc.) so they don't pile up forever in the user's browser.
   */
  setWithExpiry(key, value, ttlMs) {
    if (!hasStorage) return false;
    try {
      const envelope = JSON.stringify({ v: value, exp: Date.now() + ttlMs });
      window.localStorage.setItem(key, envelope);
      return true;
    } catch {
      return false;
    }
  },
  getWithExpiry(key) {
    if (!hasStorage) return null;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      // Backwards-compat: tolerate plain string values that were written
      // before the envelope format (return them as-is, no expiry).
      let parsed;
      try { parsed = JSON.parse(raw); } catch { return raw; }
      if (typeof parsed !== "object" || parsed === null || !("v" in parsed) || !("exp" in parsed)) {
        return raw;
      }
      if (Date.now() > parsed.exp) {
        try { window.localStorage.removeItem(key); } catch { /* ignore */ }
        return null;
      }
      return parsed.v;
    } catch {
      return null;
    }
  },
};

export default safeStorage;
export { hasStorage };
